import {
  createSelector,
  MemoizedSelector
} from '@ngrx/store';
import { selectDigitaixState } from '../core.selectors';
import {
  RequiredDocumentMeta,
  RequireId,
  FireStoreDocument
} from '@digitaix/types';

export const selectDocumentState = createSelector(
  selectDigitaixState,
  (state) => state && state.documents,
);

export const selectCollection = (collectionPath: string) =>
  createSelector(
    selectDocumentState,
    (state) => (state && state[collectionPath]) || null,
  );

export const isCollectionLoaded = (collectionPath: string) =>
  createSelector(
    selectCollection(collectionPath),
    (collection) => !!collection,
  );

export const getAllDocuments: <T extends FireStoreDocument>(
  collectionPath: string,
  archived?: boolean,
) => MemoizedSelector<any, Array<RequiredDocumentMeta<T>>> = <T extends FireStoreDocument>(
  collectionPath: string,
  archived: boolean = false,
) =>
  createSelector(
    selectCollection(collectionPath),
    (collection) => {
      let documents: Array<RequiredDocumentMeta<T>> = [];

      if (collection && collection.entities) {
        if (Object.keys(collection.entities).length) {
          documents = Object.entries(collection.entities).map(
            ([id, document]) => document,
          );
          if (archived === false) {
            documents = documents.filter((doc) => !doc.__archived);
          }
        }
      }

      return documents;
    },
  );

export const getDocumentById = <T = any>(collectionPath: string, id: string) =>
  createSelector(
    selectCollection(collectionPath),
    (collection) => (collection && (collection.entities[id] as RequiredDocumentMeta<T>)) || null,
  );

export const getDocumentsByIds = <T = any>(
  collectionPath: string,
  ids: string[],
) =>
  createSelector(
    selectCollection(collectionPath),
    (collection) => {
      if (collection && collection.entities && ids && ids.length) {
        if (Object.keys(collection.entities).length) {
          return Object.entries(collection.entities)
            .filter(([id, document]) => ids.includes(id))
            .map(([id, document]) => document) as Array<RequiredDocumentMeta<T>>;
        }
      }

      return [] as Array<RequiredDocumentMeta<T>>;
    },
  );

export const countDocuments = (collectionPath: string) =>
  createSelector(
    selectCollection(collectionPath),
    (collection) => collection && collection.ids.length,
  );
